import { useTranslation } from 'react-i18next';
import { useUpdateResetExchangeRate } from '@legacy/apis/exchangeRateAPI';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@legacy/components/mui-components';
import { Dispatch, FC, SetStateAction } from 'react';
import { handleError } from '../../utils/serverResponseHandler';

interface ResetExchangeRateModalProps {
  periodId: number;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const ResetExchangeRateModal: FC<ResetExchangeRateModalProps> = ({
  periodId,
  isOpen,
  setIsOpen,
}) => {
  const { t } = useTranslation('systemAdminExchangeRate');
  const { mutateAsync: updateReset, isPending: isResetLoading } =
    useUpdateResetExchangeRate();

  const updateResetExchangeRateAction = async () => {
    try {
      const response = await updateReset(periodId, {
        onError: (error) => {
          handleError(error, t);
        },
      });
      if (response) {
        return response;
      }
    } catch (e) {
      // TODO: We should notify the user that an error has occurred
      // for now we are just logging the error as we don't have a reasonable way to
      // tell what went wrong.
      // eslint-disable-next-line no-console
      console.error('Error updating reset exchange rate', e);
    } finally {
      setIsOpen(false);
    }
    return null;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="dialog-reset-exchange-rate"
      aria-describedby="dialog-reset-exchange-rate-desc"
    >
      <DialogTitle id="dialog-reset-exchange-rate">
        {t('modals.ResetExchangeRatesTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="dialog-reset-exchange-rate-desc">
          {t('modals.ResetExchangeRatesDescription')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setIsOpen(false)}
          data-automation-id="ResetExchangeRateModalCancelButton"
        >
          {t('modals.CancelButton')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => updateResetExchangeRateAction()}
          autoFocus
          data-automation-id="ResetExchangeRateModalSaveButton"
          disabled={isResetLoading}
        >
          {t('modals.ResetExchangeRatesSaveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetExchangeRateModal;
